import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Card, Spinner } from 'react-bootstrap'
import { useLocation, useParams } from 'react-router-dom'
import useFetch from '../../../../_fetch'
import PageNumber from '../../_page-number/page-number'
import { PageNumberProvider } from '../../_page-number/page-number-context'
import { IPartnership } from '../types'

const CDN_SCRIPT = '<script src="https://cdn.nordpar.com/partnership.js" defer></script>'

function PartnershipPrintHeader({ section }: { section: string }) {
    return (
        <>
            <div className="partnership-print-report-header">
                <div className="d-flex align-items-center justify-content-between gap-3">
                    <h6 className="mb-0">Sale Partnership Setup</h6>
                    <img src="/nordpar-logo.png" alt="Nordpar" className="partnership-print-logo" />
                </div>
                <div className="partnership-print-header-rule" data-report-header-rule="true" />
            </div>
            <div className="partnership-print-eyebrow mb-3">{section}</div>
        </>
    )
}

const PartnershipPrint: React.FC = () => {
    const { id } = useParams()
    const location = useLocation()
    const { doFetch } = useFetch()

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [partnership, setPartnership] = useState<IPartnership | null>(null)

    useEffect(() => {
        const fetchPartnership = async () => {
            try {
                setLoading(true)
                setError('')

                const documentToken = new URLSearchParams(location.search).get('documentToken')
                const endpoint = documentToken
                    ? `/user/partnerships/${id}/document-data?token=${encodeURIComponent(documentToken)}`
                    : `/user/partnerships/${id}`
                const res = await doFetch(endpoint)
                if (res.status >= 400) throw await res.text()

                setPartnership(await res.json())
            } catch (err: any) {
                setError(typeof err === 'string' ? err : 'Could not load partnership')
            } finally {
                setLoading(false)
            }
        }

        if (id) fetchPartnership()
    }, [doFetch, id, location.search])

    const clientSaleValues = useMemo(() => {
        if (!partnership) return ''
        return `<script>
${partnership.allowCookielessConversions ? `window.npAgreementId = '${partnership.id}';\n` : ''}window.npOrderId = 'REPLACE_WITH_ORDER_ID';
window.npOrderValue = '0.00';
window.npOrderCurrency = '${partnership.currency || 'DKK'}';
</script>`
    }, [partnership])

    const serverApiExample = useMemo(() => {
        if (!partnership) return ''

        return JSON.stringify({
            conversionIdentifier: partnership.scriptSecret || 'SERVER_SIDE_CONVERSION_SECRET',
            ...(partnership.allowCookielessConversions ? { agreementId: partnership.id } : {
                agreementId: '<from np_partnership_attr cookie>',
                publisherUserId: '<from np_partnership_attr cookie>',
                clickRef: '<from np_partnership_attr cookie, optional>',
                capturedAt: '<from np_partnership_attr cookie>',
            }),
            orderId: 'ORDER-12345',
            amount: '99.95',
            currency: partnership.currency || 'DKK',
            eventTimestamp: new Date().toISOString(),
        }, null, 2)
    }, [partnership])

    if (loading) {
        return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
    }

    if (error) return <Alert variant="danger" className="mb-0">{error}</Alert>
    if (!partnership) return <Alert variant="warning" className="mb-0">Partnership was not found.</Alert>

    return (
        <PageNumberProvider>
            <div className="partnership-print-document">
                <div className="partnership-print-page" data-partnership-print-page="1">
                    <PageNumber />
                    <PartnershipPrintHeader section="Implementation Guide · Browser setup" />

                    <Card className="mb-3 partnership-print-cover-card">
                        <Card.Body>
                            <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                                <div>
                                    <h4 className="mb-1">Browser conversion tracking</h4>
                                    <div className="text-muted small">Partner: {partnership.name}</div>
                                </div>
                                <Badge bg="dark" className="mt-1">{partnership.type.charAt(0).toUpperCase() + partnership.type.slice(1)}</Badge>
                            </div>
                            <div className="text-muted small">Agreement ID: {partnership.id}</div>
                            <div className="text-muted small">Allowed domains: {partnership.domains?.join(', ') || 'None configured'}</div>
                            <div className="text-muted small">Keep this document private. It contains the Server-side Conversion Secret Key.</div>
                            <div className="partnership-print-note mt-3 mb-0">
                                <strong>Recommended:</strong> Use the browser setup below. It requires one reusable CDN script and three order values on the confirmation page.
                            </div>
                        </Card.Body>
                    </Card>

                    <Card>
                    <Card.Header><strong>Browser setup (recommended)</strong></Card.Header>
                    <Card.Body>
                        <h6 className="mb-2">1. Install the CDN script</h6>
                        <p className="mb-2 text-muted">
                            Add this tag to relevant landing pages and order confirmation pages. It stores <code>np-ref</code>/<code>np-click-ref</code> attribution in the first-party <code>np_partnership_attr</code> cookie.
                        </p>
                        <pre className="mb-3 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>{CDN_SCRIPT}</pre>

                        <h6 className="mb-2">2. Add the order values</h6>
                        <p className="mb-2 text-muted">
                            On the order confirmation page, add this complete block and replace the three placeholder values with values from your checkout. The CDN script continues checking for up to 3 seconds.
                        </p>
                        <pre className="mb-2 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>{clientSaleValues}</pre>
                        <div className="partnership-print-note mb-0">
                            If any value is missing, no sale request is sent. No secret is used in the browser. The browser origin must match an allowed domain above.
                            {partnership.allowCookielessConversions && <div className="mt-2">Without an attribution cookie, conversions are credited to this partnership’s publisher using <code>npAgreementId</code>. Keep that ID unchanged. For signups, use a unique signup ID as <code>npOrderId</code>, value <code>0</code> and currency <code>{partnership.currency || 'DKK'}</code>. The signup ID must stay the same on reload to prevent duplicates. An existing attribution cookie takes precedence.</div>}
                        </div>
                    </Card.Body>
                    </Card>
                </div>

                <div className="partnership-print-page page-break" data-partnership-print-page="2">
                    <PageNumber />
                    <PartnershipPrintHeader section="Implementation Guide · Server-side alternative" />
                    <Card>
                    <Card.Header><strong>Server-side alternative</strong></Card.Header>
                    <Card.Body>
                        <p className="mb-2 text-muted">
                            {partnership.allowCookielessConversions
                                ? 'The example below reports without a cookie. If cookie attribution is available, send its agreementId, publisherUserId, clickRef and capturedAt instead. Omit the browser order values to avoid reporting twice. Server requests must include the private key.'
                                : 'Keep the CDN script for attribution, but omit the browser order values and send the conversion from your backend. Server requests must include the private key.'}
                        </p>
                        <p className="mb-2 text-muted">POST endpoint: <code>https://cdn.nordpar.com/conversion</code></p>
                        <p className="mb-2 text-muted">Attribution cookie: <code>np_partnership_attr</code></p>
                        <ul className="mb-2 text-muted" style={{ paddingLeft: '1.2rem' }}>
                            <li><strong>conversionIdentifier</strong>: the Server-side Conversion Secret Key</li>
                            {partnership.allowCookielessConversions
                                ? <li><strong>agreementId</strong>: this partnership’s ID. Without a cookie, omit <strong>publisherUserId</strong>, <strong>clickRef</strong> and <strong>capturedAt</strong>; the server selects the active publisher.</li>
                                : <li><strong>agreementId</strong>, <strong>publisherUserId</strong>, <strong>clickRef</strong> and <strong>capturedAt</strong>: from the attribution cookie</li>}
                            <li><strong>orderId</strong>, <strong>amount</strong> and <strong>currency</strong>: from your order system</li>
                            <li><strong>eventTimestamp</strong>: ISO timestamp for the conversion</li>
                        </ul>
                        <pre className="mb-0 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>{serverApiExample}</pre>
                    </Card.Body>
                    </Card>
                </div>
                <span hidden data-nordpar-pdf-ready="true" />
            </div>
        </PageNumberProvider>
    )
}

export default PartnershipPrint
